import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';

import {
  prepareMaterialForUniqueExtraction,
  type TextureExportPreset,
} from './modelViewerMaterialExtraction';

import type { IAssetFile } from '@/editor/core/assetHandlers/assetHandler';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

import { reloadActorsReferencingAsset } from '@/editor/core/actions/sceneActions';
import { store } from '@/editor/store';

const MODEL_EXPORT_MIME_TYPE = 'model/gltf-binary';

export interface ModelMaterialOption {
  id: string;
  label: string;
  material: THREE.Material;
}

export interface ExtractAndApplyMaterialOverrideOptions {
  asset: IAssetFile;
  gltf: GLTF;
  material: THREE.Material;
  materialDisplayName: string;
  textureExportPreset: TextureExportPreset;
  materialBindingKey?: string;
}

export interface ExtractAndApplyMaterialOverrideResult {
  materialFileName: string;
  materialOutputPath: string;
}

export function collectModelMaterialOptions(gltf: GLTF): ModelMaterialOption[] {
  const byId = new Map<string, ModelMaterialOption>();
  let anonymousIndex = 1;

  gltf.scene.traverse(object => {
    if (!(object instanceof THREE.Mesh) || !object.material) {
      return;
    }
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (!material) {
        continue;
      }
      const key = material.uuid || `${material.name}:${anonymousIndex++}`;
      if (byId.has(key)) {
        continue;
      }
      byId.set(key, {
        id: key,
        label: material.name || `Material ${anonymousIndex++}`,
        material,
      });
    }
  });

  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
}

async function saveModelWithMaterialOverrides(assetPath: string, gltf: GLTF): Promise<void> {
  const exporter = new GLTFExporter();
  const result = await new Promise<ArrayBuffer>((resolve, reject) => {
    const exportTarget =
      gltf.scene.children.length === 1
        ? gltf.scene.children[0]
        : gltf.scene.children;

    exporter.parse(
      exportTarget,
      gltfResult => {
        if (gltfResult instanceof ArrayBuffer) {
          resolve(gltfResult);
          return;
        }
        const jsonString = JSON.stringify(gltfResult);
        const encoded = new TextEncoder().encode(jsonString);
        const arrayBuffer = new ArrayBuffer(encoded.length);
        const view = new Uint8Array(arrayBuffer);
        view.set(encoded);
        resolve(arrayBuffer);
      },
      error => reject(error),
      {
        binary: true,
        embedImages: true,
        includeCustomExtensions: true,
      }
    );
  });

  const blob = new Blob([result], { type: MODEL_EXPORT_MIME_TYPE });
  const storage = store.project.makeScopedStorageProvider();
  await storage.uploadFile(ENGINE.AssetPath.fromString(assetPath), blob);
}

export async function extractAndApplyMaterialOverride({
  asset,
  gltf,
  material,
  materialDisplayName,
  textureExportPreset,
  materialBindingKey,
}: ExtractAndApplyMaterialOverrideOptions): Promise<ExtractAndApplyMaterialOverrideResult> {
  const assetDirectory = asset.path.substring(0, asset.path.lastIndexOf('/'));
  const storage = store.project.makeScopedStorageProvider();
  const { materialFileName, materialOutputPath } = await prepareMaterialForUniqueExtraction({
    material,
    materialDisplayName,
    assetDirectory,
    storage,
    textureExportPreset,
  });

  const materialData = ENGINE.WorldSerializer.serializeMaterial(material);
  const materialJson = JSON.stringify(materialData, null, 2);
  const blob = new Blob([materialJson], { type: 'application/json' });
  await storage.uploadFile(ENGINE.AssetPath.fromString(materialOutputPath), blob);

  material.userData = material.userData || {};
  material.userData.gltfExtensions = material.userData.gltfExtensions || {};
  material.userData.gltfExtensions.GENESYS_material_overrides = {
    materialOverride: materialOutputPath,
    ...(materialBindingKey ? { materialBindingKey } : {}),
  };

  await saveModelWithMaterialOverrides(asset.path, gltf);
  ENGINE.resourceManager.invalidateModelCache(ENGINE.AssetPath.fromString(asset.path));
  await reloadActorsReferencingAsset(asset.path);

  return {
    materialFileName,
    materialOutputPath,
  };
}
